import type { ReactNode } from "react";
import { DocumentKindSchema, type CasebookResponse, type EnvironmentView, type LearnerViewInPlay, type RubricDimension, type ScenePlan } from "@pushback-and-chaos/contracts";
import { toProse } from "../../data/adapt.js";
import { CASEBOOK, PROPOSED } from "../../design/copy.js";
import type { PageItem } from "./pages.js";
import { chapterHeadings } from "./chapter-heads.js";

/**
 * THE CASEBOOK'S PAGES (2026-09-24). A finished engagement, cut into the same
 * page items the story uses, so it is paginated, measured and read exactly as
 * play is: the story chapter by chapter (a new in-game day starts a new page,
 * headed by its coded slug line, each turn under the day it began on, since
 * 2026-10-29), then the closing chapters, each on a fresh page, in the order
 * Codex set out: how it ended (only when the deadline came into it), the
 * recommendation (only when there was one), the examination (only when there
 * was one), and the review: what was true, where it turned, what the learner
 * would have found, how the work was read, and the coach's note on this
 * engagement. The closing chapters keep their coded section heads ("Your
 * recommendation.", "In the room."): they are sections, not days.
 *
 * Every word here is the record's or the case's own. Nothing numeric is drawn:
 * a reading is a word (Law 2), the tape keeps the mechanics.
 */

export interface BookChapter {
  /** The atom that opens the chapter; the contents page turns to the page it starts. */
  readonly key: string;
  readonly label: string;
}

export interface BookScene {
  readonly environment: EnvironmentView | null;
  readonly plan: ScenePlan | null;
  /** True on the review: the case's cover stands as the book's endpiece. */
  readonly cover: boolean;
}

export interface CasebookPages {
  readonly items: readonly PageItem[];
  readonly chapters: readonly BookChapter[];
  /** The first atom of the review, where "the end." turns to. */
  readonly reviewKey: string;
  readonly sceneFor: (atomKey: string | null) => BookScene;
  readonly documentsFor: (atomKey: string | null) => LearnerViewInPlay["documents"];
  readonly allDocuments: LearnerViewInPlay["documents"];
}

const SKILL_WORDS: Readonly<Record<RubricDimension, string>> = {
  STRUCTURE: "structure",
  QUANTITATIVE: "quantitative",
  JUDGMENT: "judgment",
  COMMUNICATION: "communication",
  AMBIGUITY_TOLERANCE: "ambiguity tolerance",
  CALIBRATION: "calibration",
};

/** "23 September", with the year only when it is not this one. */
export function bookDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString("en-GB", sameYear ? { day: "numeric", month: "long" } : { day: "numeric", month: "long", year: "numeric" });
}

/** "presented · 23 September", the byline a finished engagement carries everywhere. */
export function endingByline(ending: CasebookResponse["ending"], completedAt: string): string {
  return `${CASEBOOK.endings[ending]} · ${bookDate(completedAt)}`;
}

function turnOf(atomKey: string | null): number | null {
  if (atomKey === null) return null;
  const match = /^(?:ch|turn)-(\d+)/.exec(atomKey);
  return match?.[1] === undefined ? null : Number.parseInt(match[1], 10);
}

function block(key: string, node: () => ReactNode, breakBefore = false): PageItem {
  return { key, kind: "block", node, breakBefore };
}
function text(key: string, value: string, className: string, breakBefore = false): PageItem {
  return { key, kind: "text", text: value, className, breakBefore };
}
/** Paragraphs as written: blank lines divide them, and a line break inside one is kept by the prose class. */
function paragraphs(value: string): readonly string[] {
  return value.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p.length > 0);
}

export function casebookPages(book: CasebookResponse): CasebookPages {
  const items: PageItem[] = [];
  const chapters: BookChapter[] = [];

  // THE STORY, day by day: a turn under the day it began on, as the live story places it (chapter-heads.ts).
  const headings = chapterHeadings(book.turns.map((turn) => turn.chapter));
  for (const [index, turn] of book.turns.entries()) {
    const key = `turn-${String(turn.turn)}`;
    const title = headings[index] ?? null;
    if (title !== null) {
      const headKey = `ch-${String(turn.turn)}`;
      items.push(block(headKey, () => <h2 className="pcs-story-title">{title}</h2>, items.length > 0));
      chapters.push({ key: headKey, label: title });
    }
    // A turn is an exchange: where a page may begin once the chapter runs long, and what "the newest" means.
    if (turn.learnerText !== null) items.push({ ...text(`${key}:said`, turn.learnerText, "pcs-said"), anchor: true });
    turn.narration.forEach((line, i) => { items.push(text(`${key}:n${String(i)}`, String(toProse(line)), "pcs-prose")); });
    if (turn.turn > 0) {
      turn.documents.forEach((doc, i) => {
        items.push(block(`${key}:d${String(i)}`, () => <p className="pcs-aside"><i>{doc.title}</i> lands on the desk.</p>));
      });
    }
    turn.npcTells.forEach((tell, i) => { items.push(text(`${key}:t${String(i)}`, tell, "pcs-aside")); });
  }

  // HOW IT ENDED, when the deadline came into it (the debrief's own words).
  if (book.ending !== "presented") {
    const title = book.ending === "time_expired" ? PROPOSED.debriefEnding : PROPOSED.debriefEndingAtDeadline;
    const lead = book.ending === "time_expired" ? PROPOSED.debriefEndingLead : PROPOSED.debriefEndingAtDeadlineLead;
    items.push(block("end:head", () => (
      <>
        <h2 className="pcs-story-title">{title}</h2>
        <p className="pcs-book-lead">{lead}</p>
      </>
    ), true));
    chapters.push({ key: "end:head", label: title });
    book.openThreads.forEach((thread, i) => { items.push(text(`end:thread${String(i)}`, thread, "pcs-prose pcs-book-thread")); });
  }

  // THE RECOMMENDATION, as submitted: a paper within the book.
  if (book.recommendation !== null) {
    const parts = paragraphs(book.recommendation);
    items.push(block("rec:head", () => (
      <>
        <h2 className="pcs-story-title">{CASEBOOK.recommendationTitle}</h2>
        <p className="pcs-book-lead">{CASEBOOK.recommendationLead}</p>
        <div className="pcs-book-paper-top">
          <span>{CASEBOOK.recommendationKind}</span>
        </div>
      </>
    ), true));
    chapters.push({ key: "rec:head", label: CASEBOOK.recommendationTitle });
    parts.forEach((part, i) => { items.push(text(`rec:p${String(i)}`, part, i === parts.length - 1 ? "pcs-book-paper-text pcs-book-paper-last" : "pcs-book-paper-text")); });
  }

  // THE EXAMINATION: the partner's questions in the book's voice, the learner's answers set right.
  if (book.crossExam.exchanges.length > 0) {
    const examiner = book.crossExam.examiner;
    items.push(block("exam:head", () => <h2 className="pcs-story-title">{CASEBOOK.examTitle}</h2>, true));
    chapters.push({ key: "exam:head", label: CASEBOOK.examTitle });
    book.crossExam.exchanges.forEach((exchange, i) => {
      items.push(block(`exam:q${String(i)}:who`, () => <p className="pcs-book-speaker">{examiner}</p>));
      items.push(text(`exam:q${String(i)}`, exchange.question, "pcs-prose"));
      items.push(text(`exam:a${String(i)}`, exchange.answer, "pcs-said"));
    });
  }

  // THE REVIEW. Truth and hindsight first, then the Assessor's reading, then the coach.
  const review = book.review;
  const reviewKey = "rev:head";
  items.push(block(reviewKey, () => (
    <>
      <p className="pcs-label pcs-book-eyebrow">{CASEBOOK.afterEyebrow}</p>
      <h2 className="pcs-story-title">{CASEBOOK.truth}</h2>
    </>
  ), true));
  chapters.push({ key: reviewKey, label: CASEBOOK.afterContents });
  items.push(text("rev:truth", review.truth.mechanism, "pcs-prose"));
  if (review.truth.whyNonObvious.length > 0) items.push(text("rev:truth-why", review.truth.whyNonObvious, "pcs-prose pcs-book-soft"));

  if (review.turned.length > 0) {
    items.push(block("rev:turned", () => <h3 className="pcs-book-section">{CASEBOOK.turned}</h3>));
    review.turned.forEach((moment, i) => { items.push(text(`rev:turned${String(i)}`, moment, "pcs-prose")); });
  }
  if (review.wouldHaveFound.length > 0) {
    items.push(block("rev:found", () => <h3 className="pcs-book-section">{CASEBOOK.wouldHaveFound}</h3>));
    review.wouldHaveFound.forEach((found, i) => {
      items.push(block(`rev:found${String(i)}:thread`, () => <p className="pcs-book-thread-name">{found.thread}</p>));
      items.push(text(`rev:found${String(i)}`, found.reveal, "pcs-prose"));
    });
  }

  const reading = review.reading;
  items.push(block("rev:read", () => <h3 className="pcs-book-section">{CASEBOOK.read}</h3>));
  if (reading.caseFocus !== null) items.push(text("rev:focus", reading.caseFocus, "pcs-prose"));
  if (reading.overall !== null) items.push(text("rev:overall", reading.overall, "pcs-prose"));
  reading.skills.forEach((skill) => {
    items.push(block(`rev:skill-${skill.dimension}`, () => (
      <p className="pcs-book-reading">
        <span>{SKILL_WORDS[skill.dimension]}</span>
        <span>{skill.word}</span>
      </p>
    )));
    if (skill.reason !== null) items.push(text(`rev:skill-${skill.dimension}:why`, skill.reason, "pcs-book-reason"));
  });

  const note = review.coachNote;
  items.push(block("rev:coach", () => (
    <div className="pcs-book-coach">
      <h3 className="pcs-book-section">{CASEBOOK.coach}</h3>
      {note === null ? <p className="pcs-book-coach-text pcs-book-soft">{CASEBOOK.coachMissing}</p> : (
        <>
          <p className="pcs-book-coach-text">{note.text}</p>
          {note.addedLater ? <p className="pcs-book-coach-added">{CASEBOOK.coachAdded(bookDate(note.writtenAt))}</p> : null}
        </>
      )}
    </div>
  )));

  // THE ROOM AND THE DESK FOR A PAGE: as the log stood at that page's turn (the server draws a turn that
  // crosses into a new day at the moment it began, so its room agrees with the day it is printed under).
  const lastTurn = book.turns.at(-1)?.turn ?? 0;
  const sceneAt = (turn: number): BookScene => {
    for (let i = book.turns.length - 1; i >= 0; i -= 1) {
      const t = book.turns[i];
      if (t === undefined || t.turn > turn) continue;
      if (t.environment !== null) return { environment: t.environment, plan: t.turn === turn ? t.plan : null, cover: false };
    }
    return { environment: null, plan: null, cover: false };
  };
  const allDocuments: LearnerViewInPlay["documents"] = book.turns.flatMap((t) =>
    t.documents.flatMap((doc) => {
      const kind = DocumentKindSchema.safeParse(doc.kind);
      return kind.success ? [{ id: doc.docId, kind: kind.data, title: doc.title, fidelity: "CLEAN" as const }] : [];
    }),
  );
  const deliveredBy = new Map<string, number>();
  for (const t of book.turns) for (const doc of t.documents) if (!deliveredBy.has(doc.docId)) deliveredBy.set(doc.docId, t.turn);

  // THE CLOSING CHAPTERS (how it ended, the recommendation, the examination) have no day heading: they
  // stand in the room the story's last turn left (the server's closingEnvironment, the room the
  // recommendation was made in), with that turn's staging; without one, the last turn's room.
  const closingScene = (): BookScene => {
    const last = sceneAt(lastTurn);
    return book.closingEnvironment === null ? last : { ...last, environment: book.closingEnvironment };
  };
  const sceneFor = (atomKey: string | null): BookScene => {
    if (atomKey === null) return sceneAt(0);
    if (atomKey.startsWith("rev:")) return { environment: null, plan: null, cover: true };
    const turn = turnOf(atomKey);
    return turn === null ? closingScene() : sceneAt(turn);
  };
  const documentsFor = (atomKey: string | null): LearnerViewInPlay["documents"] => {
    const turn = atomKey === null ? 0 : (turnOf(atomKey) ?? Number.POSITIVE_INFINITY);
    return allDocuments.filter((doc) => (deliveredBy.get(doc.id) ?? 0) <= turn);
  };

  return { items, chapters, reviewKey, sceneFor, documentsFor, allDocuments };
}
